import React, { useState } from 'react';
import { 
  Users, Plus, Eye, Search, Filter, MapPin, Briefcase, 
  Mail, DollarSign, Calendar, Edit, Sparkles
} from 'lucide-react';
import AISourcingFromCandidatesTab from '../ai-sourcing/AISourcingFromCandidatesTab';
import ViewToggle from '../shared/ViewToggle';
import StatusBadge from '../shared/StatusBadge';
import Pagination from '../shared/Pagination';
import CandidateDetailModal from './CandidateDetailModal';
import CandidateFormComprehensive from '../CandidateFormComprehensive';
import TeamInfoBanner from './TeamInfoBanner';
import { 
  formatSalary, 
  formatExperience, 
  formatDateAdded, 
  isRecentlyAdded, 
  getTopSkills 
} from '../../utils/formatters';

const CandidatesTab = ({ selectedUser, candidates, submissions = [], myTeam, teamLead, onRefresh, onAddCandidate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [candidateToEdit, setCandidateToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all_time'); // Default to All Time to show all candidates including newly created
  const [itemsPerPage, setItemsPerPage] = useState(20); // User-selectable pagination
  const [showAISourcingModal, setShowAISourcingModal] = useState(false);
  const [allCandidates, setAllCandidates] = useState([]); // For "All Candidates" view
  const [isLoadingAllCandidates, setIsLoadingAllCandidates] = useState(false);

  // Handle edit candidate
  const handleEditCandidate = (candidate) => {
    setCandidateToEdit(candidate);
    setIsEditModalOpen(true);
    setIsModalOpen(false); // Close detail modal if open
  };

  // Handle edit success
  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setCandidateToEdit(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  // Fetch all candidates when switching to "All Candidates" view
  React.useEffect(() => {
    const fetchAllCandidates = async () => {
      if (viewMode === 'all' && allCandidates.length === 0) {
        setIsLoadingAllCandidates(true);
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
          const response = await fetch(`${BACKEND_URL}/api/candidates?limit=25000`);
          if (response.ok) {
            const data = await response.json();
            setAllCandidates(data);
            console.log('[CandidatesTab] Fetched all candidates:', data.length);
          }
        } catch (error) {
          console.error('[CandidatesTab] Error fetching all candidates:', error);
        } finally {
          setIsLoadingAllCandidates(false);
        }
      }
    };

    fetchAllCandidates();
  }, [viewMode, allCandidates.length]);

  // Time frame options for date filtering (This Month first, YTD last)
  const timeframeOptions = [
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_12_months', label: 'Last 12 Months' },
    { value: 'all_time', label: 'All Time' },
    { value: 'ytd', label: 'YTD' }
  ];

  // Calculate date range based on timeframe
  const getDateRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (selectedTimeframe) {
      case 'ytd':
        return { start: new Date(currentYear, 0, 1), end: now };
      case 'this_month':
        return { start: new Date(currentYear, now.getMonth(), 1), end: now };
      case 'last_month':
        const lastMonth = new Date(currentYear, now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(currentYear, now.getMonth(), 0);
        return { start: lastMonth, end: lastMonthEnd };
      case 'last_6_months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        return { start: sixMonthsAgo, end: now };
      case 'last_12_months':
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setMonth(now.getMonth() - 12);
        return { start: twelveMonthsAgo, end: now };
      case 'all_time':
        return { start: new Date(2000, 0, 1), end: now };
      default:
        return { start: new Date(currentYear, 0, 1), end: now };
    }
  };

  // Determine if candidate belongs to current user
  // Uses consistent recruiter matching utility (UUID-based with name fallback)
  const isMyCandidate = React.useCallback((candidate) => {
    if (!selectedUser) return false;
    
    // Import utility for consistent matching
    const { isCandidateOwnedByUser } = require('../../utils/recruiterUtils');
    
    // Use standardized matching logic
    if (isCandidateOwnedByUser(candidate, selectedUser)) return true;
    
    // Additional: Check if user has submitted this candidate
    if (submissions && submissions.length > 0) {
      const userId = selectedUser.id;
      const userName = selectedUser.full_name || selectedUser.name;
      const hasSubmission = submissions.some(s => 
        s.candidate_id === candidate.id && (
          s.submitted_by_id === userId ||
          s.submitted_by_name === userName
        )
      );
      if (hasSubmission) return true;
    }
    
    return false;
  }, [selectedUser, submissions]);

  // Debug logging for candidate filtering
  React.useEffect(() => {
    console.log('┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
    console.log('┃ [CandidatesTab] DATA RECEIVED FROM PARENT      ┃');
    console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
    console.log('[CandidatesTab] Selected User:', selectedUser?.full_name);
    console.log('[CandidatesTab] View Mode:', viewMode);
    console.log('[CandidatesTab] Total Candidates:', candidates.length);
    console.log('[CandidatesTab] Total Submissions:', submissions.length);
    
    if (candidates.length > 0) {
      console.log('[CandidatesTab] Sample candidates (first 3):');
      candidates.slice(0, 3).forEach((c, idx) => {
        console.log(`  [${idx + 1}] ${c.name} (ID: ${c.id}, Recruiter: ${c.primary_recruiter_name || c.recruiter_name || 'N/A'})`);
      });
    }
    
    if (submissions.length > 0) {
      const aiSourced = submissions.filter(s => s.submission_source === 'ai_suggested');
      console.log('[CandidatesTab] AI-sourced submissions:', aiSourced.length);
      if (aiSourced.length > 0) {
        console.log('[CandidatesTab] Latest AI-sourced:', {
          candidate_name: aiSourced[0].candidate_name,
          candidate_id: aiSourced[0].candidate_id,
          job_title: aiSourced[0].job_title
        });
      }
    }
    
    console.log('┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
    console.log('┃ [CandidatesTab] END DATA LOG                   ┃');
    console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
  }, [candidates.length, submissions.length, viewMode, selectedUser?.id]);

  // Apply filters and sort by most recently added
  // Use allCandidates when in "All" view, otherwise use candidates from props (user's candidates)
  const candidateSource = viewMode === 'all' ? allCandidates : candidates;
  
  const filteredCandidates = candidateSource
    .filter(candidate => {
      // Filter by ownership (my candidates vs all candidates)
      const matchesOwnership = viewMode === 'all' || isMyCandidate(candidate);
      
      const matchesSearch = searchTerm === '' || 
        (candidate.display_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.name || `${candidate.first_name} ${candidate.last_name}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.current_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.current_company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Match status exactly as stored in database (case-sensitive)
      const matchesStatus = statusFilter === 'all' || 
        (candidate.candidate_status || '') === statusFilter;
      
      // NEW: Time filter - filter by date_added (from CSV "Date Added" column)
      const dateRange = getDateRange();
      const candidateDate = new Date(candidate.date_added || candidate.created_at || 0);
      const matchesTimeframe = candidateDate >= dateRange.start && candidateDate <= dateRange.end;
      
      return matchesOwnership && matchesSearch && matchesStatus && matchesTimeframe;
    })
    .sort((a, b) => {
      // Sort by date_added (actual add date from CSV), newest first
      const dateA = new Date(a.date_added || a.created_at || 0);
      const dateB = new Date(b.date_added || b.created_at || 0);
      return dateB - dateA; // Descending order (newest first)
    });

  // Pagination
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCandidates = filteredCandidates.slice(startIndex, startIndex + itemsPerPage);

  // All formatting functions now imported from shared utilities
  
  return (
    <div className="space-y-4" data-testid="candidates-tab">
      {/* Team Info Banner - Shows for recruiters */}
      <TeamInfoBanner selectedUser={selectedUser} teamLead={teamLead} myTeam={myTeam} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {viewMode === 'my' ? 'My Candidates' : 'All Candidates'} ({filteredCandidates.length})
              </h3>
              <p className="text-sm text-gray-600">
                {viewMode === 'my' ? 'Your assigned talent pipeline' : 'Company-wide talent pool'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <ViewToggle
              leftLabel="My Candidates"
              rightLabel="All Candidates"
              activeView={viewMode}
              onToggle={setViewMode}
              leftValue="my"
              rightValue="all"
              activeColor="bg-blue-600"
            />
            
            <button
              onClick={() => setShowAISourcingModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors"
              data-testid="ai-source-btn"
            >
              <Sparkles className="h-4 w-4" />
              AI Source Candidates
            </button>
            
            <button
              onClick={onAddCandidate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="add-candidate-btn"
            >
              <Plus className="h-4 w-4" />
              Add Candidate
            </button>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID, name, title, company, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-600" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="New Lead">New Lead</option>
              <option value="Hired">Hired</option>
              <option value="sourced">Sourced</option>
              <option value="DNU">DNU</option>
            </select>
          </div>
        </div>

        {/* Time Slicer */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              Candidates Added: {timeframeOptions.find(opt => opt.value === selectedTimeframe)?.label}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {timeframeOptions.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  setSelectedTimeframe(option.value);
                  setCurrentPage(1); // Reset to first page when filter changes
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === option.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                }`}
                data-testid={`timeframe-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Candidate Name
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Current Title
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Primary Owner
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Date Added
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Skills
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Candidate Status
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoadingAllCandidates ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <p className="text-gray-600 font-medium">Loading all candidates...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedCandidates.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Users className="h-12 w-12 text-gray-400" />
                      <div>
                        <p className="text-gray-600 font-medium">No candidates found</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {searchTerm || statusFilter !== 'all' 
                            ? 'Try adjusting your filters'
                            : 'Start by adding your first candidate'}
                        </p>
                      </div>
                      {!searchTerm && statusFilter === 'all' && (
                        <button
                          onClick={onAddCandidate}
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Add Candidate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedCandidates.map((candidate, index) => {
                  const isNew = isRecentlyAdded(candidate);
                  
                  return (
                    <tr 
                      key={candidate.id} 
                      className={`group hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-150 cursor-pointer ${
                        isNew ? 'bg-gradient-to-r from-green-50 to-emerald-50' : ''
                      }`}
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setIsModalOpen(true);
                      }}
                      data-testid={`candidate-row-${index}`}
                    >
                    {/* Candidate ID */}
                    <td className="px-4 py-3">
                      {candidate.display_id ? (
                        <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-purple-100 text-purple-800 rounded">
                          {candidate.display_id}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-red-100 text-red-800 rounded" title="Missing display_id - contact support">
                          ERROR
                        </span>
                      )}
                    </td>
                    
                    {/* Candidate Name */}
                    <td className="px-4 py-3">
                      <div>
                        <button className="text-blue-600 hover:text-blue-800 font-medium text-left">
                          {candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown'}
                        </button>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          {candidate.email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    
                    {/* Source Column - Clean differentiation */}
                    <td className="px-4 py-3">
                      {(candidate.source === 'ai_sourcing' || candidate.created_via === 'ai_sourcing') ? (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-md">
                          <Sparkles className="h-3.5 w-3.5 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">AI Sourced</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md">
                          <Users className="h-3.5 w-3.5 text-gray-600" />
                          <span className="text-xs font-medium text-gray-700">Internal</span>
                        </div>
                      )}
                    </td>

                    {/* Current Title */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700">
                        {candidate.current_title || 'Not specified'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {candidate.current_company || 'N/A'}
                      </div>
                    </td>

                    {/* Location */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-3 w-3" />
                        {candidate.location || 'N/A'}
                      </div>
                    </td>

                    {/* Primary Owner */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-700">
                        <Users className="h-3 w-3 text-gray-400" />
                        {(() => {
                          const { getCandidateRecruiterName } = require('../../utils/recruiterUtils');
                          return getCandidateRecruiterName(candidate);
                        })()}
                      </div>
                    </td>

                    {/* Date Added */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <span className={`text-xs font-medium ${
                          isNew ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {formatDateAdded(candidate)}
                        </span>
                        {isNew && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            NEW
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Skills */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {candidate.skills && candidate.skills.length > 0 ? (
                          <>
                            {candidate.skills.slice(0, 3).map((skill, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-md font-medium whitespace-nowrap"
                                title={skill}
                              >
                                {skill.length > 12 ? `${skill.substring(0, 12)}...` : skill}
                              </span>
                            ))}
                            {candidate.skills.length > 3 && (
                              <span 
                                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md font-medium"
                                title={`${candidate.skills.slice(3).join(', ')}`}
                              >
                                +{candidate.skills.length - 3}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No skills</span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge status={candidate.candidate_status || 'New Lead'} />
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <button 
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View Details"
                          onClick={() => {
                            setSelectedCandidate(candidate);
                            setIsModalOpen(true);
                          }}
                          data-testid={`view-candidate-${candidate.id}`}
                        >
                          <Eye className="h-4 w-4 text-gray-600" />
                        </button>
                        <button 
                          className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Candidate"
                          onClick={() => handleEditCandidate(candidate)}
                          data-testid={`edit-candidate-${candidate.id}`}
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredCandidates.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1); // Reset to page 1 when changing items per page
          }}
        />
      </div>

      {/* Candidate Detail Modal */}
      <CandidateDetailModal
        candidate={selectedCandidate}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCandidate(null);
        }}
        submissions={submissions}
        onEdit={handleEditCandidate}
        selectedUser={selectedUser}
      />

      {/* Candidate Edit Modal */}
      {isEditModalOpen && candidateToEdit && (
        <CandidateFormComprehensive
          candidate={candidateToEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setCandidateToEdit(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
      
      {/* AI Sourcing Modal */}
      {showAISourcingModal && (
        <AISourcingFromCandidatesTab
          selectedUser={selectedUser}
          myTeam={myTeam}
          teamLead={teamLead}
          onClose={() => setShowAISourcingModal(false)}
          onComplete={(jobId, runId) => {
            // Optionally refresh candidates after sourcing
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default CandidatesTab;
